/**
 * Shared app-wide state (Zustand). Mirrors Redux state written from Router and app entry.
 * Only `language` is persisted (key: portal-language). All other slices are in-memory.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const LANGUAGE_STORAGE_KEY = 'portal-language';

export type LocaleCode = 'en' | 'th';

type StoredObject = Record<string, unknown> | null;

export interface AppStoreState {
  affiliate: Record<string, unknown> | null;
  user: Record<string, unknown> | null;
  location: Record<string, unknown>;
  systemSettings: Record<string, unknown>;
  holidays: unknown[];
  firstload: boolean;
  language: LocaleCode;

  // Setters take any object: callers hold typed interfaces (UserProfile, Affiliate, …), which TS
  // won't assign to Record<string, unknown>. The store keeps them as an untyped bag either way.
  setAffiliate: (affiliate: object | null) => void;
  setUser: (user: object | null) => void;
  setLocation: (location: Record<string, unknown>) => void;
  setSystemSettings: (systemSettings: Record<string, unknown>) => void;
  setHolidays: (holidays: unknown[]) => void;
  setFirstLoad: (firstload: boolean) => void;
  setLanguage: (language: LocaleCode) => void;
}

const initialState = {
  affiliate: null as Record<string, unknown> | null,
  user: null as Record<string, unknown> | null,
  location: {} as Record<string, unknown>,
  systemSettings: {} as Record<string, unknown>,
  holidays: [] as unknown[],
  firstload: false,
  language: 'th' as LocaleCode,
};

export const useAppStore = create<AppStoreState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        setAffiliate: (affiliate) => set({ affiliate: affiliate as StoredObject }, false, 'setAffiliate'),
        setUser: (user) => set({ user: user as StoredObject }, false, 'setUser'),
        setLocation: (location) => set({ location }, false, 'setLocation'),
        setSystemSettings: (systemSettings) => set({ systemSettings }, false, 'setSystemSettings'),
        setHolidays: (holidays) => set({ holidays }, false, 'setHolidays'),
        setFirstLoad: (firstload) => set({ firstload }, false, 'setFirstLoad'),
        setLanguage: (language) => set({ language }, false, 'setLanguage'),
      }),
      {
        name: LANGUAGE_STORAGE_KEY,
        partialize: (state) => ({ language: state.language }),
      },
    ),
    { name: 'app-store' },
  ),
);

/** Non-React: get current language (e.g. for app init or legacy sync). */
export function getLanguageCode(): LocaleCode {
  return useAppStore.getState().language;
}
